import {
	AppButton,
	CountBadge,
	TwoPaneMenu,
	TwoPaneMenuCategory
} from '@clickguard/design-system'
import ChevronDown from '@untitled-ui/icons-react/build/cjs/ChevronDown'
import { useEffect, useRef, useState } from 'react'
import {
	CONTINENTS,
	groupCountryNames
} from 'components/country-select/countries'

/** Match the criterion's inner control row, rather than the drawer's outer edge. */
const DEFAULT_PANEL_WIDTH = 380
const CATEGORIES_WIDTH = '44%'
/** What the DS draws a category row at, inside a 4px column inset. */
const CATEGORY_ROW = 36
const CATEGORIES_INSET = 4
/** Locked pane height, so walking the continents never resizes the card. */
const PANE_HEIGHT = CONTINENTS.length * CATEGORY_ROW + CATEGORIES_INSET * 2
/** The DS pins its popover trigger in an `inline-flex w-fit` span it does not
 *  expose, so reach through from the wrapper. */
const TRIGGER_FIT =
	'w-full [&>span]:w-full [&>span>div]:w-full [&_button]:w-full [&_button]:justify-between'
/** The panel portals to `document.body` with an inline `z-index: 50`, so inside the
 *  drawer's z-[9999] root it opens UNDER the drawer. The DS exposes no portal
 *  container here, so lift it — `!important`, to beat an inline style. */
const PANEL_LIFT = '!z-[10000]'
/** How many entries to spell out on the trigger before switching to a count. */
const MAX_INLINE = 2
/** The "All of <continent>" row: an ordinary item handled in `onSelectionChange`,
 *  added to the DS's selection only for display. */
const ALL_ID = '__all__'

/** Where a country name sits, for turning stored names back into menu rows. */
const PLACE_OF = new Map<string, { continentId: string; id: string }>()
CONTINENTS.forEach((continent) => {
	continent.countries.forEach((country) => {
		PLACE_OF.set(country.label, { continentId: continent.id, id: country.id })
	})
})

interface CountryPickerProps {
	/** Country NAMES, not ids — every reader of a criterion value reads it straight. */
	values: string[]
	onChange: (values: string[]) => void
	/** Trigger text when nothing is picked. */
	placeholder: string
	ariaLabel: string
	className?: string
}

/**
 * Country value picker: continents left, that continent's countries as checkbox rows
 * right, one search field across all of them. The same DS `TwoPaneMenu` the AI
 * Protection page's region control uses — separate rather than a mode on it because
 * there, no country means the whole world; here it means the criterion is unanswered.
 */
export const CountryPicker = ({
	values,
	onChange,
	placeholder,
	ariaLabel,
	className
}: CountryPickerProps) => {
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLDivElement>(null)
	const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH)

	useEffect(() => {
		const container = triggerRef.current?.parentElement
		if (!container) return
		const measure = () => {
			const style = getComputedStyle(container)
			const width =
				container.clientWidth -
				parseFloat(style.paddingLeft) -
				parseFloat(style.paddingRight)
			if (width > 0) setPanelWidth(width)
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(container)
		return () => observer.disconnect()
	}, [])

	// Stored names, back into the ids the menu draws its ticks from.
	const selection: Record<string, string[]> = {}
	values.forEach((name) => {
		const place = PLACE_OF.get(name)
		if (!place) return
		selection[place.continentId] = [
			...(selection[place.continentId] || []),
			place.id
		]
	})

	// Escape belongs to the panel: the drawer's guard only recognises menus portaled
	// into its root, and this one is on `document.body`. Capture phase, because the
	// drawer listens on the document too.
	useEffect(() => {
		if (!open) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return
			event.stopPropagation()
			setOpen(false)
		}
		document.addEventListener('keydown', onKeyDown, true)
		return () => document.removeEventListener('keydown', onKeyDown, true)
	}, [open])

	const categories: TwoPaneMenuCategory[] = CONTINENTS.map((continent) => ({
		id: continent.id,
		label: continent.label,
		// The DS counts the synthetic "All of …" row too, so count the real countries.
		badge: <CountBadge count={(selection[continent.id] || []).length} />,
		items: [
			{
				id: ALL_ID,
				label: `All of ${continent.label}`,
				searchText: `All of ${continent.label}`
			},
			...continent.countries.map((country) => ({
				id: country.id,
				label: country.label
			}))
		]
	}))

	const shown: Record<string, string[]> = {}
	CONTINENTS.forEach((continent) => {
		const ids = selection[continent.id] || []
		if (!ids.length) return
		shown[continent.id] =
			ids.length === continent.countries.length ? [ALL_ID, ...ids] : ids
	})

	// Fully picked continents collapse to "Europe (all)", as the summary folds them.
	const parts = groupCountryNames(values).map((group) =>
		group.whole ? `${group.label} (all)` : group.label
	)
	const summary = !parts.length
		? placeholder
		: parts.length <= MAX_INLINE
		? parts.join(', ')
		: `${values.length} countries`

	return (
		<div ref={triggerRef} className={`${TRIGGER_FIT} ${className ?? ''}`}>
			<TwoPaneMenu
				className={PANEL_LIFT}
				trigger={
					<AppButton
						type="button"
						variant="secondary"
						size="md"
						roundness="default"
						label={
							<span
								className={`min-w-0 truncate ${
									parts.length ? '' : 'text-[rgb(var(--cg-text-tertiary))]'
								}`}
							>
								{summary}
							</span>
						}
						rightIcon={<ChevronDown />}
						selected={open}
						aria-label={ariaLabel}
					/>
				}
				categories={categories}
				selectionMode="multiple"
				selectedItemIds={shown}
				onSelectionChange={(next, changed) => {
					const continent = CONTINENTS.find(
						(entry) => entry.id === changed.categoryId
					)
					if (!continent) return

					const picked =
						changed.item.id === ALL_ID
							? changed.selected
								? continent.countries.map((country) => country.id)
								: []
							: (next[continent.id] || []).filter((id) => id !== ALL_ID)

					const byContinent = { ...selection, [continent.id]: picked }
					// CONTINENTS order rather than click order, so the set always reads the same way.
					const ticked = CONTINENTS.flatMap((entry) =>
						entry.countries
							.filter((country) =>
								(byContinent[entry.id] || []).includes(country.id)
							)
							.map((country) => country.label)
					)
					// Values this menu cannot place, carried through untouched: the list above is
					// rebuilt from CONTINENTS on every tick, so a territory or a legacy `Global` used
					// to be rebuilt AWAY. Appended, since they have no continent to sort them among.
					const unplaced = values.filter((name) => !PLACE_OF.has(name))
					onChange([...ticked, ...unplaced])
				}}
				open={open}
				onOpenChange={setOpen}
				searchPlaceholder="Search countries"
				// Search INSIDE the card: the default bordered field sits in the panel's gutter.
				searchVariant="bare"
				itemsPaneFill="canvas"
				// Align with the operator + country controls inside the padded criterion.
				align="end"
				panelWidth={panelWidth}
				categoriesWidth={CATEGORIES_WIDTH}
				paneHeight={PANE_HEIGHT}
			/>
		</div>
	)
}
